import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Money } from '../api/client'
import s from './priceFormat.module.scss'

/**
 * Gemeinsames Handwerkszeug der beiden Kurslisten.
 *
 * Steht hier und nicht doppelt in beiden Ansichten: Ein Betrag, der in der
 * einen Liste anders aussieht als in der anderen, wäre beim Vergleichen ein
 * Ärgernis.
 */

export const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'short',
})

export const tag = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })

const zahl = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})

const menge = new Intl.NumberFormat('de-DE')

//: Eine Nachkommastelle — fuer gekuerzte Grossbetraege.
const gerundet = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1, maximumFractionDigits: 1,
})

/**
 * Ein Kurs, deutsch formatiert.
 *
 * Der Wert kommt als ZEICHENKETTE vom Server — dort ist er ein Decimal. Für
 * die Anzeige wird er in eine Zahl gewandelt; gerechnet wird damit nicht, und
 * kopiert wird die ursprüngliche Zeichenkette, nicht diese Darstellung.
 */
export function betrag(value: string): string {
  const numerisch = Number(value)
  return Number.isFinite(numerisch) ? zahl.format(numerisch) : value
}

/**
 * Wie die Zahl eines Betrags geschrieben wird.
 *
 * DER EINZIGE UNTERSCHIED zwischen den Geldzellen dieser Anwendung. Ein Kurs
 * steht mit zwei Nachkommastellen da, eine Bilanzkennzahl gekürzt als
 * „477,63 Mrd", eine Rechtsbelastung als „1 Mrd USD" — die STRUKTUR darunter
 * ist überall dieselbe. Deshalb ist sie hier ein Parameter und die Struktur
 * eine Komponente, statt viermal nachgebaut zu werden.
 */
export type Zahlform = (wert: string, waehrung: string) => string

/** Die Vorgabe: ein Kurs mit seiner Währung — „456,75 USD". */
export const alsKurs: Zahlform = (wert, waehrung) =>
  `${betrag(wert)} ${waehrung}`

/**
 * Ein großer Betrag, gekürzt: „253,3 Mrd. USD".
 *
 * Die ganze Zahl liest niemand: „253.259.161.613" sagt weniger als
 * „253,3 Mrd.", und in eine Tabellenspalte passt sie ohnehin nicht. Die
 * Stelle hinter dem Komma bleibt, damit zwei Titel derselben Größenordnung
 * unterscheidbar sind.
 *
 * Steht HIER und nicht dreimal in den Ansichten: Kennzahlen, Rechtsbelastung
 * und Score-Terme kürzen dieselben Größenordnungen, und ein Betrag, der in
 * der einen Ansicht anders aussieht als in der anderen, wäre beim
 * Vergleichen ein Ärgernis.
 */
export const alsGrossbetrag: Zahlform = (wert, waehrung) => {
  const n = Number(wert)
  if (!Number.isFinite(n)) return `${wert} ${waehrung}`
  const STUFEN: [number, string][] = [
    [1e12, 'Bio.'], [1e9, 'Mrd.'], [1e6, 'Mio.'], [1e3, 'Tsd.'],
  ]
  for (const [teiler, einheit] of STUFEN) {
    if (Math.abs(n) >= teiler) {
      return `${gerundet.format(n / teiler)} ${einheit} ${waehrung}`
    }
  }
  return `${menge.format(Math.round(n))} ${waehrung}`
}

type Zeile = { schluessel: string; text: string; titel?: string }

/**
 * Die Zeilen eines Betrags — DER UMGERECHNETE ZUERST.
 *
 * WER „Umrechnen in EUR" WÄHLT, WILL EURO LESEN. Bis hierher stand der
 * gehandelte Betrag oben und die Umrechnung darunter; beim Überfliegen einer
 * Spalte musste das Auge dann jedes Mal in die zweite Zeile springen, um den
 * Wert zu finden, nach dem gefragt wurde.
 *
 * Das `≈` bleibt am gerechneten Wert und sagt weiterhin, welcher der
 * abgeleitete ist — und das Original steht direkt darunter, nicht
 * verschwunden.
 *
 * OHNE UMRECHNUNG bleibt genau eine Zeile: das Original, und zwar in der
 * kräftigen Schrift. Was oben steht, ist immer der Wert, der gilt.
 *
 * Der Kurs und sein Datum stehen im TITEL, nicht in der Zeile: Sie
 * beantworten eine Rückfrage ("womit gerechnet?"), nicht die Hauptfrage, und
 * würden die Spalte sonst überladen.
 */
function geldzeilen(wert: Money, schreibe: Zahlform,
                    bis?: Money | null): Zeile[] {
  // Eine Spanne teilt sich Währung und Kurs — beide Kanten stehen in
  // derselben. Deshalb genügt EINE umgerechnete Zeile je Zielwährung.
  const spanne = (links: string | undefined, rechts: string | undefined,
                  waehrung: string) =>
    links && rechts
      ? `${betrag(links)}–${schreibe(rechts, waehrung)}`
      : schreibe((links ?? rechts)!, waehrung)

  const gerechnet = wert.currencies.map((um) => ({
    schluessel: um.currency,
    text: `≈ ${spanne(um.amount, kante(bis, um.currency), um.currency)}`,
    titel: `Kurs ${um.rate} vom ${tag.format(new Date(um.rate_date))}`,
  }))
  return [...gerechnet, {
    schluessel: 'original',
    text: spanne(wert.amount, bis?.amount, wert.currency),
  }]
}

/** Der Betrag der anderen Kante in einer Zielwährung — oder nichts. */
const kante = (wert: Money | null | undefined, waehrung: string) =>
  wert?.currencies.find((k) => k.currency === waehrung)?.amount

/**
 * Ein Geldbetrag in einer Tabellenzelle: der gewählte Wert oben, das Original
 * darunter und gedämpft.
 *
 * `bis` macht daraus eine SPANNE — „219,13–219,32 USD". Beide Kanten laufen
 * durch dieselbe Zeilenlogik, damit eine Spanne nicht anders aussieht als ein
 * einzelner Betrag.
 *
 * `links` für die Stellen, an denen der Betrag nicht in einer rechtsbündigen
 * Spalte steht, sondern in einer Reihe von Eckdaten: Dort begänne er sonst
 * eingerückt neben seinen linksbündigen Nachbarn.
 */
export function Geld({ wert, bis, schreibe = alsKurs, links }: {
  wert: Money
  bis?: Money | null
  schreibe?: Zahlform
  links?: boolean
}) {
  const [oben, ...darunter] = geldzeilen(wert, schreibe, bis)
  return (
    <span className={`${s.geld} ${links ? s.geldLinks : ''}`}>
      <span className={s.gilt} title={oben.titel}>{oben.text}</span>
      {darunter.map((z) => (
        <span key={z.schluessel} className={s.nebenher} title={z.titel}>
          {z.text}
        </span>
      ))}
    </span>
  )
}

/** Kurzform für den Normalfall: ein Kurs in einer Tabellenzelle. */
export function geld(wert: Money): ReactNode {
  return <Geld wert={wert} />
}

/**
 * Derselbe Betrag für den FLIESSTEXT: „219,13 USD ≈ 187,20 EUR".
 *
 * `Geld` setzt den gerechneten Wert über das Original — richtig in einer
 * Tabellenspalte, falsch in einem Satz: „Spread höchstens ⏎ ≈ 0,17 EUR ⏎ 0,20
 * USD ⏎ bei Ausbruch" wäre kein Satz mehr.
 *
 * HIER STEHT DAS ORIGINAL ZUERST, anders als in der Zelle. Ein Satz wird
 * gelesen und nicht überflogen; das nachgestellte „≈ …" liest sich als das,
 * was es ist — eine Ergänzung.
 *
 * Zeichenkette statt ReactNode, weil die Aufrufer Sätze zusammensetzen.
 */
export function geldtext(wert: Money): string {
  const umgerechnet = wert.currencies
    .map((um) => ` ≈ ${betrag(um.amount)} ${um.currency}`)
  return `${betrag(wert.amount)} ${wert.currency}${umgerechnet.join('')}`
}

export function stueck(value: number): string {
  return menge.format(value)
}

/**
 * Das Zeichen zu einem Währungscode — oder der Code selbst.
 *
 * NUR FÜR DIE VERBREITETEN. Für Won, Rupie oder Lira gibt es zwar Zeichen,
 * aber sie sind hier weniger vertraut als der Code: „1.783 KRW" liest sich
 * eindeutiger als „1.783 ₩". Wer den Code sieht, weiß, dass er nachsehen
 * muss; wer ein unbekanntes Zeichen sieht, rät.
 *
 * NACHGESTELLT verwendet, nicht vorangestellt: „12,50 €" ist die deutsche
 * Schreibweise, und die Tabelle ist auf Deutsch gesetzt.
 */
export function waehrungszeichen(code: string | null | undefined): string {
  if (!code) return ''
  const ZEICHEN: Record<string, string> = {
    EUR: '€', USD: '$', GBP: '£', JPY: '¥', CHF: 'CHF',
  }
  return ZEICHEN[code] ?? code
}

/** Ein Kurs mit seiner Währung — „12,50 €". */
export function kurs(value: string, waehrung?: string | null): string {
  const zeichen = waehrungszeichen(waehrung)
  return zeichen ? `${betrag(value)} ${zeichen}` : betrag(value)
}

/**
 * Eine Marktkapitalisierung, gekürzt und mit ihrer Währung.
 *
 * GEKÜRZT, weil die ganze Zahl niemand liest: „1.783.152.400.000.000" sagt
 * weniger als „1.783,2 Bio.", und in einer Tabellenspalte passt sie ohnehin
 * nicht. Die Stelle hinter dem Komma bleibt, damit zwei Titel derselben
 * Größenordnung noch unterscheidbar sind.
 *
 * MIT WÄHRUNG, immer. Ohne sie ist die Zahl irreführend statt hilfreich:
 * Samsungs 1.783 Bio. Won sind weniger als Apples 3,9 Bio. Dollar. Wer die
 * Größen vergleichen will, nimmt die Klasse — die ist dafür gemacht.
 */
export function kapitalisierung(value: string | null | undefined,
                                waehrung: string | null | undefined): string {
  if (!value || !waehrung) return '—'
  const numerisch = Number(value)
  if (!Number.isFinite(numerisch)) return value

  const STUFEN: [number, string][] = [
    [1e12, 'Bio.'], [1e9, 'Mrd.'], [1e6, 'Mio.'], [1e3, 'Tsd.'],
  ]
  for (const [teiler, einheit] of STUFEN) {
    if (Math.abs(numerisch) >= teiler) {
      return `${gerundet.format(numerisch / teiler)} ${einheit} ${waehrung}`
    }
  }
  return `${menge.format(Math.round(numerisch))} ${waehrung}`
}

/**
 * Der Instrumentname als Verweis auf seine Detailansicht.
 *
 * Ein Link und kein Zeilenklick: In einer Kursliste stehen zu einem Titel
 * viele Zeilen — die ganze Zeile anklickbar zu machen hieße, dass jede von
 * ihnen zum selben Ziel führt. Der Name ist die Stelle, die es meint.
 */
export function InstrumentLink({ id, name }: { id: number; name: string }) {
  return (
    <Link to={`/instruments/${id}`} className={s.link}
          onClick={(event) => event.stopPropagation()}>
      {name}
    </Link>
  )
}


/**
 * Die Zielwährung einer Ansicht — für die Kopfzeile.
 *
 * DIE AUSWAHL KOMMT AUS DEN DATEN, nicht aus einer Liste im Code: Was nicht
 * in `t_fx_rate` steht, lässt sich nicht umrechnen und soll gar nicht erst
 * wählbar sein. Dasselbe hält `fx_rate_repository.pairs()` schon fest.
 *
 * Ohne Kurse bleibt das Feld abgeschaltet statt zu verschwinden: Ein Feld,
 * das mal da ist und mal nicht, lässt einen suchen.
 */
export function WaehrungsWahl({ waehrungen, wert, setzen }: {
  waehrungen: string[]
  wert: string
  setzen: (wert: string) => void
}) {
  return (
    <label className={s.waehrungswahl}>
      <span className={s.waehrungslabel}>Umrechnen in</span>
      <select value={wert} disabled={waehrungen.length === 0}
              onChange={(event) => setzen(event.target.value)}>
        <option value="">—</option>
        {waehrungen.map((w) => <option key={w} value={w}>{w}</option>)}
      </select>
    </label>
  )
}
